// Wer aus einer Explosion etwas abbekommt — die Regel der Flaechenkanaele.
//
// Rakete und Nova buchten ihren Schaden bis hierher **in der Szene**
// (`SpaceScene.explode`), und damit hinter Phaser: kein Node-Test fuehrt diesen
// Pfad aus. Das war die offene Haelfte von FG-47 — das Gate sah, dass die
// Rakete einen Gegner *anfliegt*, aber nicht, dass er etwas abbekommt. Zwei
// typ- und lint-saubere Regressionen (Brocken vor Gegnern lenken; die
// Gegnerschleife hinter die Abbausperre ziehen) liessen die ganze Suite gruen.
//
// Also zieht die **Entscheidung** hierher: welche Ziele eine Explosion trifft
// und mit welchem Anteil. Die Szene bekommt das Ergebnis und macht daraus
// Schaden und Bilder. Dieselbe Bewegung wie bei `guided.ts` — dort die
// Zielwahl der Rakete, hier ihr Einschlag.
//
// Diese Datei importiert **kein Phaser** (Gate FG-18).

import { clamp } from '../core/math';

/** Das Wenige, das eine Explosion von einem Ziel braucht. */
export interface BlastTarget {
  id: string;
  x: number;
  y: number;
  r: number;
}

/** Ein getroffenes Ziel und sein Anteil am vollen Schaden. */
export interface BlastHit<T extends BlastTarget> {
  target: T;
  share: number;
}

/**
 * Der Anteil eines Ziels am vollen Schaden — nach Abstand, nie unter einem
 * Viertel.
 *
 * Die Untergrenze ist Absicht: eine Explosion, die am Rand auf null auslaeuft,
 * fuehlt sich an wie ein Fehlschuss, obwohl sie getroffen hat.
 */
export function blastShare(dist: number, radius: number, r: number): number {
  return clamp(1 - dist / (radius + r), 0.25, 1);
}

/**
 * Wen diese Explosion trifft — **Gegner immer, Brocken nur ausserhalb des
 * Gefechts**.
 *
 * Dieselbe Regel wie `area_locked` im Server-Tick: steht ein Gegner im
 * Wirkbereich der Flaechenwaffen, bekommen die Brocken dort nichts mehr. Vorher
 * hiess „Vorrang" nur „zuerst".
 *
 * Der Rueckgabewert ist die **Zahl der gefuellten Plaetze**, nicht ein neues
 * Array: `out` wird wiederverwendet und wird nie gekuerzt, damit auch seine
 * Eintraege stehen bleiben (Gate FG-14). Die Nova feuert im Takt, und ein
 * Einschlag bei Vollausbau trifft Dutzende Ziele.
 *
 * Der Puffer ist zugleich die noetige **Momentaufnahme**: das Buchen zerstoert
 * Brocken und mutiert damit die Liste, aus der er stammt.
 */
export function splitBlast<T extends BlastTarget>(
  x: number,
  y: number,
  radius: number,
  list: readonly T[],
  out: BlastHit<T>[],
): number {
  let n = 0;
  for (const target of list) {
    const share = blastShare(Math.hypot(target.x - x, target.y - y), radius, target.r);
    const slot = out[n];
    if (slot) {
      slot.target = target;
      slot.share = share;
    } else {
      out.push({ target, share });
    }
    n++;
  }
  return n;
}

/**
 * Wohin der Schaden einer Explosion geht — und die beiden Puffer dafuer.
 *
 * Die Szene haelt **eine** solche Senke als Feld, wie `missileGuide`: ihre
 * Methoden buchen, ihre Puffer werden wiederverwendet (Gate FG-14).
 */
export interface BlastSink<H extends BlastTarget, R extends BlastTarget> {
  hostile(target: H, share: number): void;
  rock(target: R, share: number): void;
  readonly foeBuf: BlastHit<H>[];
  readonly rockBuf: BlastHit<R>[];
}

/**
 * Eine Explosion abrechnen: erst die Gegner, dann — **ausserhalb des
 * Gefechts** — die Brocken.
 *
 * Die Schleifen stehen hier und nicht in der Szene, und das ist der ganze
 * Zweck dieser Datei. In `SpaceScene.explode` waren sie hinter Phaser
 * eingeschlossen: ein Handgriff, der die Gegnerschleife stilllegt, liess die
 * gesamte Suite gruen — auch FG-47, das Gate gegen genau diesen Fehler. Hier
 * fuehrt der Test sie aus.
 */
export function applyBlast<H extends BlastTarget, R extends BlastTarget>(
  x: number,
  y: number,
  radius: number,
  hostiles: readonly H[],
  rocks: readonly R[],
  engaged: boolean,
  sink: BlastSink<H, R>,
): void {
  const foes = splitBlast(x, y, radius, hostiles, sink.foeBuf);
  // **Der Abbau pausiert im Gefecht** (`GAME-001`): steht ein Gegner im
  // Wirkbereich, bekommen die Brocken dort nichts mehr.
  const stones = engaged ? 0 : splitBlast(x, y, radius, rocks, sink.rockBuf);

  for (let i = 0; i < foes; i++) sink.hostile(sink.foeBuf[i].target, sink.foeBuf[i].share);
  for (let i = 0; i < stones; i++) sink.rock(sink.rockBuf[i].target, sink.rockBuf[i].share);
}
